import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Calculator, Printer, Mail, Users, CheckCircle, Loader2,
  Download, Send, Sparkles, GraduationCap
} from 'lucide-react';
import api from '../lib/api';

// Cores vibrantes para cada número
const CORES_TABUADA = {
  0: { bg: 'from-slate-500 to-slate-600', light: 'bg-slate-100', text: 'text-slate-700' },
  1: { bg: 'from-red-500 to-red-600', light: 'bg-red-100', text: 'text-red-700' },
  2: { bg: 'from-orange-500 to-orange-600', light: 'bg-orange-100', text: 'text-orange-700' },
  3: { bg: 'from-amber-500 to-amber-600', light: 'bg-amber-100', text: 'text-amber-700' },
  4: { bg: 'from-yellow-500 to-yellow-600', light: 'bg-yellow-100', text: 'text-yellow-700' },
  5: { bg: 'from-lime-500 to-lime-600', light: 'bg-lime-100', text: 'text-lime-700' },
  6: { bg: 'from-green-500 to-green-600', light: 'bg-green-100', text: 'text-green-700' },
  7: { bg: 'from-teal-500 to-teal-600', light: 'bg-teal-100', text: 'text-teal-700' },
  8: { bg: 'from-cyan-500 to-cyan-600', light: 'bg-cyan-100', text: 'text-cyan-700' },
  9: { bg: 'from-blue-500 to-blue-600', light: 'bg-blue-100', text: 'text-blue-700' },
  10: { bg: 'from-purple-500 to-purple-600', light: 'bg-purple-100', text: 'text-purple-700' },
};

// Componente de uma tabuada individual
const TabuadaCard = ({ numero }) => {
  const cores = CORES_TABUADA[numero];
  
  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-slate-100 hover:shadow-xl transition-shadow">
      {/* Cabeçalho colorido */}
      <div className={`bg-gradient-to-r ${cores.bg} p-4 text-center`}>
        <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-2">
          <span className="text-4xl font-bold text-white">{numero}</span>
        </div>
        <h3 className="text-white font-bold text-lg">Tabuada do {numero}</h3>
      </div>
      
      {/* Corpo com os cálculos */}
      <div className="p-4">
        <div className="space-y-2">
          {[...Array(11)].map((_, i) => (
            <div 
              key={i}
              className={`flex items-center justify-between p-2 rounded-lg ${i % 2 === 0 ? cores.light : 'bg-white'} transition-colors`}
            >
              <span className={`font-medium ${cores.text}`}>
                {numero} × {i}
              </span>
              <span className="font-bold text-slate-800 text-lg">
                = {numero * i}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Tabuada = () => {
  const [turmas, setTurmas] = useState([]);
  const [turmaSelecionada, setTurmaSelecionada] = useState('');
  const [alunosTurma, setAlunosTurma] = useState([]);
  const [tabuadasSelecionadas, setTabuadasSelecionadas] = useState([1, 2, 3, 4, 5, 6, 7, 8, 9, 10]);
  const [showEnvioModal, setShowEnvioModal] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [loadingTurmas, setLoadingTurmas] = useState(true);

  // Carregar turmas
  useEffect(() => {
    const loadTurmas = async () => {
      try {
        const response = await api.get('/classes');
        setTurmas(response.data || []);
      } catch (error) {
        console.error('Erro ao carregar turmas:', error);
      } finally {
        setLoadingTurmas(false);
      }
    };
    loadTurmas();
  }, []);

  // Carregar alunos da turma selecionada
  useEffect(() => {
    const loadAlunos = async () => {
      if (!turmaSelecionada) {
        setAlunosTurma([]);
        return;
      }
      try {
        const response = await api.get(`/students?class_id=${turmaSelecionada}`);
        setAlunosTurma(response.data || []);
      } catch (error) {
        console.error('Erro ao carregar alunos:', error);
      }
    };
    loadAlunos();
  }, [turmaSelecionada]);

  const toggleTabuada = (num) => {
    setTabuadasSelecionadas(prev => 
      prev.includes(num) 
        ? prev.filter(n => n !== num)
        : [...prev, num].sort((a, b) => a - b)
    );
  };

  const selecionarTodas = () => {
    setTabuadasSelecionadas([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]);
  };

  const limparSelecao = () => {
    setTabuadasSelecionadas([]);
  };

  const imprimirTabuada = () => {
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Tabuada de Multiplicação - DNA.IA</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            padding: 20px;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .header {
            text-align: center;
            margin-bottom: 20px;
            padding-bottom: 15px;
            border-bottom: 3px solid #8E52AB;
          }
          .header h1 { color: #8E52AB; font-size: 28px; margin-bottom: 5px; }
          .header p { color: #64748b; }
          .grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
          }
          .card {
            border: 2px solid #e2e8f0;
            border-radius: 12px;
            overflow: hidden;
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .card-header {
            padding: 15px;
            text-align: center;
            color: white;
          }
          .card-header .number {
            font-size: 32px;
            font-weight: bold;
            display: block;
          }
          .card-header .title {
            font-size: 14px;
            font-weight: 600;
          }
          .card-body { padding: 10px; }
          .row {
            display: flex;
            justify-content: space-between;
            padding: 6px 10px;
            border-radius: 6px;
            font-size: 14px;
          }
          .row:nth-child(even) { background: #f1f5f9; }
          .row .result { font-weight: bold; font-size: 16px; }
          @media print {
            @page { margin: 1cm; size: A4; }
            body { padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>📐 Tabuada de Multiplicação</h1>
          <p>Material de apoio pedagógico - DNA.IA</p>
        </div>
        <div class="grid">
          ${tabuadasSelecionadas.map(num => `
            <div class="card">
              <div class="card-header" style="background: linear-gradient(135deg, ${getGradientColor(num)});">
                <span class="number">${num}</span>
                <span class="title">Tabuada do ${num}</span>
              </div>
              <div class="card-body">
                ${[...Array(11)].map((_, i) => `
                  <div class="row">
                    <span>${num} × ${i}</span>
                    <span class="result">= ${num * i}</span>
                  </div>
                `).join('')}
              </div>
            </div>
          `).join('')}
        </div>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 300);
  };

  const getGradientColor = (num) => {
    const colors = {
      0: '#64748b, #475569',
      1: '#ef4444, #dc2626',
      2: '#f97316, #ea580c',
      3: '#f59e0b, #d97706',
      4: '#eab308, #ca8a04',
      5: '#84cc16, #65a30d',
      6: '#22c55e, #16a34a',
      7: '#14b8a6, #0d9488',
      8: '#06b6d4, #0891b2',
      9: '#3b82f6, #2563eb',
      10: '#8E52AB, #7B3F98',
    };
    return colors[num] || colors[1];
  };

  const enviarParaTurma = async () => {
    if (!turmaSelecionada) {
      toast.error('Selecione uma turma');
      return;
    }

    if (alunosTurma.length === 0) {
      toast.error('Nenhum aluno encontrado na turma');
      return;
    }

    setEnviando(true);
    
    try {
      const htmlContent = `
        <h2 style="color: #8E52AB; text-align: center;">📐 Tabuada de Multiplicação</h2>
        <p style="text-align: center; color: #64748b;">Material de apoio pedagógico</p>
        <hr style="margin: 20px 0; border: none; border-top: 2px solid #e2e8f0;">
        ${tabuadasSelecionadas.map(num => `
          <div style="margin-bottom: 20px;">
            <h3 style="color: ${num <= 5 ? '#16a34a' : '#8E52AB'};">Tabuada do ${num}</h3>
            <table style="width: 100%; border-collapse: collapse;">
              ${[...Array(11)].map((_, i) => `
                <tr style="background: ${i % 2 === 0 ? '#f8fafc' : '#ffffff'};">
                  <td style="padding: 8px; border: 1px solid #e2e8f0;">${num} × ${i}</td>
                  <td style="padding: 8px; border: 1px solid #e2e8f0; font-weight: bold;">= ${num * i}</td>
                </tr>
              `).join('')}
            </table>
          </div>
        `).join('')}
      `;

      const turmaInfo = turmas.find(t => t.id === turmaSelecionada);
      const emailsEnviados = [];
      
      for (const aluno of alunosTurma) {
        const email = aluno.email || aluno.guardian_email;
        if (email) {
          try {
            await api.post('/send-email', {
              to_email: email,
              subject: `📐 Tabuada de Multiplicação - ${turmaInfo?.name || 'Sua turma'}`,
              html_content: htmlContent,
            });
            emailsEnviados.push(aluno.name);
          } catch (e) {
            console.error(`Erro ao enviar para ${aluno.name}:`, e);
          }
        }
      }

      toast.success(`Tabuada enviada para ${emailsEnviados.length} aluno(s)!`);
      setShowEnvioModal(false);
    } catch (error) {
      console.error('Erro ao enviar:', error);
      toast.error('Erro ao enviar tabuada');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calculator className="w-7 h-7 text-purple-600" />
            Gerador de Tabuada
          </h1>
          <p className="text-muted-foreground mt-1">
            Crie tabuadas visuais para imprimir ou enviar aos alunos
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={imprimirTabuada}
            disabled={tabuadasSelecionadas.length === 0}
            className="gap-2"
          >
            <Printer className="w-4 h-4" />
            Imprimir
          </Button>
          <Button 
            onClick={() => setShowEnvioModal(true)}
            disabled={tabuadasSelecionadas.length === 0}
            className="gap-2 bg-purple-600 hover:bg-purple-700"
          >
            <Mail className="w-4 h-4" />
            Enviar por E-mail
          </Button>
        </div>
      </div>

      {/* Seleção de Tabuadas */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Selecione as Tabuadas</CardTitle>
          <CardDescription>Escolha quais tabuadas deseja incluir (0 a 10)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 mb-4">
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => {
              const cores = CORES_TABUADA[num];
              const isSelected = tabuadasSelecionadas.includes(num);
              
              return (
                <button
                  key={num}
                  onClick={() => toggleTabuada(num)}
                  className={`
                    w-14 h-14 rounded-xl font-bold text-xl transition-all
                    ${isSelected 
                      ? `bg-gradient-to-br ${cores.bg} text-white shadow-lg scale-105` 
                      : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                    }
                  `}
                >
                  {num}
                </button>
              );
            })}
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={selecionarTodas}>
              Selecionar Todas
            </Button>
            <Button variant="outline" size="sm" onClick={limparSelecao}>
              Limpar Seleção
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview das Tabuadas */}
      {tabuadasSelecionadas.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            Pré-visualização ({tabuadasSelecionadas.length} tabuadas)
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {tabuadasSelecionadas.map(num => (
              <TabuadaCard key={num} numero={num} />
            ))}
          </div>
        </div>
      )}

      {tabuadasSelecionadas.length === 0 && (
        <div className="text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed">
          <Calculator className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Selecione pelo menos uma tabuada para visualizar</p>
        </div>
      )}

      {/* Modal de Envio por E-mail */}
      <Dialog open={showEnvioModal} onOpenChange={setShowEnvioModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-purple-600" />
              Enviar Tabuada por E-mail
            </DialogTitle>
            <DialogDescription>
              Selecione a turma para enviar a tabuada aos alunos
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Turma</Label>
              <Select value={turmaSelecionada} onValueChange={setTurmaSelecionada}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma turma..." />
                </SelectTrigger>
                <SelectContent>
                  {turmas.map(turma => (
                    <SelectItem key={turma.id} value={turma.id}>
                      {turma.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {turmaSelecionada && (
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                  <Users className="w-4 h-4" />
                  <span>{alunosTurma.length} aluno(s) na turma</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-purple-600">
                  <CheckCircle className="w-4 h-4" />
                  <span>{tabuadasSelecionadas.length} tabuada(s) selecionada(s)</span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEnvioModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={enviarParaTurma} 
              disabled={enviando || !turmaSelecionada}
              className="gap-2 bg-purple-600 hover:bg-purple-700"
            >
              {enviando ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Enviar para Turma
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tabuada;
